import type { Rect } from '../../../engine/hex/types';
import type { IconRegistry } from '../../../ui/overlay/assets/IconRegistry';
import type { ModalTheme } from '../ModalTheme';
import type { HitTarget, MeasureResult, UiItem } from './Types';

type ThemeFn<T> = T | ((theme: ModalTheme) => T);
function resolve<T>(v: ThemeFn<T> | undefined, theme: ModalTheme, fallback: T): T {
    if (v === undefined) return fallback;
    if (typeof v === 'function') return (v as (t: ModalTheme) => T)(theme);
    return v;
}

export type BoxStyle = {
    padding?: ThemeFn<number>; // default 0
    fill?: ThemeFn<string | null>;
    stroke?: ThemeFn<string | null>;
    strokeWidth?: ThemeFn<number>;
    widthMode?: 'fill' | 'wrap'; // default fill
};

export class BoxItem implements UiItem {
    private rect: Rect = { x: 0, y: 0, w: 0, h: 0 };
    private pad = 0;
    private fill: string | null = null;
    private stroke: string | null = null;
    private strokeWidth = 1;
    private widthMode: 'fill' | 'wrap' = 'fill';

    private childSize: MeasureResult = { w: 0, h: 0 };

    constructor(
        private child: UiItem,
        private style: BoxStyle = {},
    ) {}

    measure(ctx: CanvasRenderingContext2D, maxW: number, theme: ModalTheme): MeasureResult {
        this.pad = resolve(this.style.padding, theme, 0);
        this.fill = resolve(this.style.fill, theme, null);
        this.stroke = resolve(this.style.stroke, theme, null);
        this.strokeWidth = resolve(this.style.strokeWidth, theme, 1);
        this.widthMode = this.style.widthMode ?? 'fill';

        const innerW = Math.max(0, maxW - this.pad * 2);
        this.childSize = this.child.measure(ctx, innerW, theme);

        const w =
            this.widthMode === 'wrap' ? Math.min(maxW, this.childSize.w + this.pad * 2) : maxW;

        const h = this.childSize.h + this.pad * 2;
        return { w, h };
    }

    layout(x: number, y: number, w: number, theme: ModalTheme): void {
        const innerW = Math.max(0, w - this.pad * 2);
        const h = this.childSize.h + this.pad * 2;

        this.rect = { x, y, w, h };
        this.child.layout(x + this.pad, y + this.pad, innerW, theme);
    }

    draw(ctx: CanvasRenderingContext2D, theme: ModalTheme, icons?: IconRegistry): void {
        if (this.fill || this.stroke) {
            ctx.lineWidth = this.strokeWidth;
            if (this.fill) {
                ctx.fillStyle = this.fill;
                ctx.fillRect(this.rect.x, this.rect.y, this.rect.w, this.rect.h);
            }
            if (this.stroke) {
                ctx.strokeStyle = this.stroke;
                ctx.strokeRect(this.rect.x, this.rect.y, this.rect.w, this.rect.h);
            }
        }
        this.child.draw(ctx, theme, icons);
    }

    collectHitTargets(out: HitTarget[], scrollY: number): void {
        this.child.collectHitTargets(out, scrollY);
    }
}
